import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';
import './Checkout.css';

function Checkout() {
  const { cartItems, clearCart } = useCart();
  const navigate = useNavigate();
  const { currencySymbol } = useSettings();
  const [pageData, setPageData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'India',
    notes: ''
  });
  const [cartId, setCartId] = useState('');
  const [orderId, setOrderId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [orderSuccess, setOrderSuccess] = useState(false);

  // Calculate total price
  const calculateTotal = () => {
    return cartItems.reduce((total, item) => {
      const price = parseFloat(item.price.replace(/,/g, ''));
      return total + price;
    }, 0).toLocaleString('en-IN');
  };

  // Fetch checkout page data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get('/api/checkout');
        setPageData(response.data);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching checkout data:', err);
        setError('Failed to load checkout data. Please try again later.');
        setIsLoading(false);
      }
    };

    fetchData();

    // Create a cart in the backend if items exist
    const createCart = async () => {
      if (cartItems.length > 0) {
        try {
          const response = await axios.post('/api/carts', {
            items: cartItems.map(item => ({
              id: item.id,
              name: item.name,
              price: parseFloat(item.price.replace(/,/g, ''))
            })),
            total: parseFloat(calculateTotal().replace(/,/g, ''))
          });
          setCartId(response.data.cart_id);
        } catch (err) {
          console.error('Error creating cart:', err);
          setError('Failed to initialize checkout. Please try again.');
        }
      } else if (!orderSuccess) {
        // Only redirect to cart page if no items AND not a successful order
        navigate('/cart');
      }
    };

    createCart();
  }, [cartItems, navigate, orderSuccess]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Format the data according to the backend's OrderData model
      const orderData = {
        // Form fields directly at the root level
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip, // Note: backend expects 'zip_code', not 'zip'
        country: formData.country,
        notes: formData.notes || "",
        
        // Cart data in a nested object
        cart_data: {
          items: cartItems.map(item => ({
            id: item.id,
            name: item.name,
            price: parseFloat(item.price.toString().replace(/,/g, '')),
            quantity: item.quantity || 1
          })),
          total: parseFloat(calculateTotal().replace(/,/g, ''))
        }
      };

      const response = await axios.post('/api/checkout', orderData);

      setOrderId(response.data.order_id);
      setOrderSuccess(true);
      // Store the full order information returned from the backend
      setPageData(prevData => ({
        ...prevData,
        orderDetails: response.data
      }));
      clearCart(); // Clear the cart after successful order
    } catch (err) {
      console.error('Error submitting order:', err);
      setError('Failed to submit your order. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open phone dialer
  const handlePhoneCall = () => {
    if (pageData && pageData.payment_info && pageData.payment_info.phone) {
      window.location.href = `tel:${pageData.payment_info.phone}`;
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div>Loading checkout information...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{error}</p>
        <Link to="/cart" className="back-button">Return to Cart</Link>
      </div>
    );
  }

  if (orderSuccess && pageData) {
    const orderDetails = pageData.orderDetails || {};
    
    return (
      <div className="checkout-success">
        <h1>{pageData.payment_info?.thank_you_message || "Thank you for your order!"}</h1>
        
        <div className="order-details">
          <p className="thank-you">Your order has been received.</p>
          <p className="order-id">Order ID: <strong>{orderId}</strong></p>
          {orderDetails.created_at && (
            <p className="order-date">Order Date: <strong>{orderDetails.created_at}</strong></p>
          )}
        </div>
        
        {orderDetails.cart && (
          <div className="order-items">
            <h2>Order Items</h2>
            <div className="order-items-list">
              {orderDetails.cart.items.map((item, index) => (
                <div key={index} className="order-item">
                  <span className="item-name">{item.name}</span>
                  <span className="item-price">{currencySymbol}{item.price.toLocaleString('en-IN')}</span>
                  {item.quantity > 1 && <span className="item-quantity">x{item.quantity}</span>}
                </div>
              ))}
            </div>
            <div className="order-total">
              <span>Total:</span>
              <span>{currencySymbol}{orderDetails.cart.total.toLocaleString('en-IN')}</span>
            </div>
          </div>
        )}
        
        <div className="payment-instructions">
          <h2>Complete Your Payment</h2>
          <p>Please call the following number to complete your payment:</p>
          <div className="payment-phone">{orderDetails.payment_phone || pageData.payment_info?.phone}</div>
          <button className="call-now-button" onClick={handlePhoneCall}>
            Call Now
          </button>
        </div>
        
        {orderDetails.email_sent === false && orderDetails.email_error && (
          <div className="email-notification warning">
            <p>Note: We couldn't send you a confirmation email due to a technical issue.</p>
            <p>Please save your order ID for reference.</p>
          </div>
        )}
        
        <div className="back-to-home">
          <Link to="/">Back to Home</Link>
        </div>
      </div>
    );
  }

  // Default form labels if pageData is not loaded yet
  const formLabels = {
    name: 'Full Name',
    email: 'Email Address',
    phone: 'Phone Number',
    address: 'Address',
    city: 'City',
    state: 'State',
    zip: 'Postal/ZIP Code',
    country: 'Country',
    notes: 'Additional Notes'
  };

  // Button labels if pageData is not loaded yet
  const buttonLabels = {
    submit: 'Complete Order',
    back: 'Back to Cart'
  };

  return (
    <div className="checkout-container">
      {isLoading ? (
        <div className="loading-container">
          <p>Loading checkout information...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p>{error}</p>
        </div>
      ) : orderSuccess ? (
        <div className="checkout-success">
          <h1>{pageData?.payment_info?.thank_you_message || "Thank you for your order!"}</h1>
          
          <div className="order-details">
            <p className="thank-you">Your order has been received.</p>
            <p className="order-id">Order ID: <strong>{orderId}</strong></p>
            {pageData?.orderDetails?.created_at && (
              <p className="order-date">Order Date: <strong>{pageData.orderDetails.created_at}</strong></p>
            )}
          </div>
          
          {pageData?.orderDetails?.cart && (
            <div className="order-items">
              <h2>Order Items</h2>
              <div className="order-items-list">
                {pageData.orderDetails.cart.items.map((item, index) => (
                  <div key={index} className="order-item">
                    <span className="item-name">{item.name}</span>
                    <span className="item-price">{currencySymbol}{item.price.toLocaleString('en-IN')}</span>
                    {item.quantity > 1 && <span className="item-quantity">x{item.quantity}</span>}
                  </div>
                ))}
              </div>
              <div className="order-total">
                <span>Total:</span>
                <span>{currencySymbol}{pageData.orderDetails.cart.total.toLocaleString('en-IN')}</span>
              </div>
            </div>
          )}
          
          <div className="payment-instructions">
            <h2>Complete Your Payment</h2>
            <p>Please call the following number to complete your payment:</p>
            <div className="payment-phone">{pageData?.orderDetails?.payment_phone || pageData?.payment_info?.phone}</div>
            <button className="call-now-button" onClick={handlePhoneCall}>
              Call Now
            </button>
          </div>
          
          {pageData?.orderDetails?.email_sent === false && pageData?.orderDetails?.email_error && (
            <div className="email-notification warning">
              <p>Note: We couldn't send you a confirmation email due to a technical issue.</p>
              <p>Please save your order ID for reference.</p>
            </div>
          )}
          
          <div className="back-to-home">
            <Link to="/">Back to Home</Link>
          </div>
        </div>
      ) : (
        <>
          <div className="checkout-header">
            <h1>{pageData?.hero?.title || "Checkout"}</h1>
            <p>{pageData?.hero?.description || "Please complete your order details below."}</p>
          </div>
          
          <div className="checkout-content">
            <div className="checkout-form-container">
              <h2>Customer Information</h2>
              
              <form className="checkout-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">{pageData?.form_fields?.name_label || "Full Name"}*</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">{pageData?.form_fields?.email_label || "Email Address"}*</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">{pageData?.form_fields?.phone_label || "Phone Number"}*</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="address">{pageData?.form_fields?.address_label || "Address"}*</label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">{pageData?.form_fields?.city_label || "City"}*</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="state">{pageData?.form_fields?.state_label || "State"}*</label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="zip">{pageData?.form_fields?.zip_label || "Postal/ZIP Code"}*</label>
                    <input
                      type="text"
                      id="zip"
                      name="zip"
                      value={formData.zip}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="country">{pageData?.form_fields?.country_label || "Country"}</label>
                    <input
                      type="text"
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="notes">{pageData?.form_fields?.notes_label || "Additional Notes"}</label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows="4"
                  ></textarea>
                </div>
                
                <div className="payment-info">
                  <h3>Payment Information</h3>
                  <p>{pageData?.payment_info?.instructions || "After submitting your order, you'll need to call to complete payment."}</p>
                  <p className="phone-number">Call: <strong>{pageData?.payment_info?.phone}</strong></p>
                </div>
                
                {cartItems.length > 0 && (
                  <div className="order-summary">
                    <h3>Order Summary</h3>
                    <ul>
                      {cartItems.map((item) => (
                        <li key={item.id}>
                          <span>{item.name}</span>
                          <span>{currencySymbol}{typeof item.price === 'string' ? item.price : item.price.toLocaleString('en-IN')}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="total">
                      <span>Total</span>
                      <span>{currencySymbol}{calculateTotal()}</span>
                    </div>
                  </div>
                )}
                
                <div className="form-actions">
                  <Link to="/cart" className="back-button">
                    {pageData?.buttons?.back || "Back to Cart"}
                  </Link>
                  <button 
                    type="submit" 
                    className="submit-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting 
                      ? "Processing..." 
                      : pageData?.buttons?.submit || "Complete Order & Call Now"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Checkout; 